import React, { useContext, useEffect, useState } from 'react';
import './OwnershipList.redesign.scss';
import { UseFormMethods } from 'react-hook-form';
import Ownership from 'src/models/tract_detail/Ownership';
import CardContainer from 'src/components/common/CardContainer.redesign';
import GlobalOwnershipCard from './OwnershipCard.redesign';
import OwnershipContactCategory from 'src/models/OwnershipContactCategory';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import { useFetch } from 'use-http';
import { Url } from 'src/api';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import Owner from 'src/models/tract_detail/Owner';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import { push } from 'connected-react-router';
import AddOwner from 'src/models/tract_detail/add_ownership_revised/AddOwner';
import AddOwnershipData from 'src/models/tract_detail/add_ownership_revised/AddOwnership';
import TractDetail from 'src/models/tract_detail/TractDetail';
import { SelectItem } from 'src/models/common/SelectItem';
import MathUtil from 'src/util/MathUtil';
import AdditionOwnershipType from 'src/models/tract_detail/AdditionalOwnershipType';
import ReactHookFormUtil from 'src/util/ReactHookFormUtil';

interface IGlobalOwnershipListStateProps extends Partial<Pick<UseFormMethods, 'register' | 'errors' | 'getValues' | 'formState' | 'reset'>> {
    tractId: number;
    projectType: 'Mineral' | 'Surface';
    ownershipType: 'Mineral' | 'Surface';
    ownerships: Array<Ownership>;
    owners: Array<Owner>;
    grossAcres: number;
    ownershipContactCategories: Array<OwnershipContactCategory>;
    namePrefix: string;
    horizonNumber?: number;
    tractDetail?: TractDetail;
    leaseBuyerList: Array<SelectItem>;
    loadSurfaceTract: () => void;
    addDeviation?: (deviationOwner: AddOwner) => void;
    onNavigate?: (path: string) => void;
    onSaveAddOwnership?: (owner: AddOwnershipData) => void;
}

const GlobalOwnershipList = (props: IGlobalOwnershipListStateProps) => {
    const { del, post, put, response } = useFetch();
    const dialogContext = useContext<dialogCreators>(DialogContext);
    const appContext = useSelector((state: IRootState) => state.appContext);
    const dispatch = useDispatch();

    const [loading, setLoading] = useState<boolean>(true);
    const [ownershipInterestInfo, setOwnershipInterestInfo] = useState<JSX.Element>(() => <div />);

    useEffect(() => {
        if (props.ownerships) {
            setLoading(false);
        }
        updateOwnershipInterest();
    }, [props.ownerships, props.grossAcres]);

    const fixNumberForUIDisplay = (value: string) => {
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue)) {
            return numericValue.toFixed(7).toString();
        } else {
            return '';
        }
    };

    const totalExecutiveRights = (ownerships: Array<Ownership>) => {
        let executiveTotal = 0;
        for (let own of ownerships) {
            // Avoid null or undefined values.
            let ownExec = parseInt(own.executiveRights + '', 10);
            executiveTotal += ownExec > 0 ? ownExec : 0;

            // Calculate additionals
            if (!!own.additionalOwnerships) {
                let addOwners = own.additionalOwnerships;
                for (let add of addOwners) {
                    // Avoid null or undefined values.
                    let addExec = parseInt(add.executiveRights + '', 10);
                    executiveTotal += addExec > 0 ? addExec : 0;
                }
            }
        }

        return executiveTotal;
    };

    const updateOwnershipInterest = () => {
        let totalInterest = 0;
        let totalAcres = 0;
        let ownerships: Array<Ownership>;

        if (props.grossAcres && !props.formState.isDirty) {
            ownerships = props.ownerships;
            props.ownerships?.forEach((field: Ownership) => {
                totalInterest += parseFloat(field.decimalInterest + '');
                totalAcres += parseFloat(field.decimalInterest + '') * props.grossAcres;

                field.additionalOwnerships?.forEach((addField: Ownership) => {
                    // TODO: Also, this is just gross. Like, nest-ception.
                    if (
                        parseInt(addField.additionalOwnershipTypeId + '', 10) === AdditionOwnershipType.Heir ||
                        parseInt(addField.additionalOwnershipTypeId + '', 10) === AdditionOwnershipType.NonExecutive
                    ) {
                        totalInterest += parseFloat(addField.decimalInterest + '');
                        totalAcres += parseFloat(addField.decimalInterest + '') * props.grossAcres;
                    }
                });
            });
        } else {
            let ref = props.getValues();
            ownerships = ReactHookFormUtil.getNestedFormValues(ref, props.namePrefix) as unknown as Array<Ownership>;
            if (ownerships?.length > 0) {
                ownerships.forEach((field: Ownership) => {
                    totalInterest += parseFloat(field.decimalInterest + '');
                    totalAcres += parseFloat(field.decimalInterest + '') * props.grossAcres;
    
                    if (field.additionalOwnerships) {
                        field.additionalOwnerships.forEach((addField: Ownership) => {
                            // TODO: Update with real values instead of hard-coded.
                            // TODO: Also, this is just gross. Like, nest-ception.
                            if (
                                parseInt(addField.additionalOwnershipTypeId + '', 10) === 1 ||
                                parseInt(addField.additionalOwnershipTypeId + '', 10) === 3
                            ) {
                                totalInterest += parseFloat(addField.decimalInterest + '');
                                totalAcres += parseFloat(addField.decimalInterest + '') * props.grossAcres;
                            }
                        });
                    }
                });
            }
        }

        const execRights = ownerships?.length > 0 ? totalExecutiveRights(ownerships) : 0;

        const status = totalInterest === 1 ? '#d1fdda' : totalInterest < 1 ? '#ffeb9c' : '#ffc7ce';
        const execStatus = execRights === 1 ? '#d1fdda' : execRights < 1 ? '#ffeb9c' : '#ffc7ce';

        const interestString = isNaN(totalInterest) ? '---' : fixNumberForUIDisplay(MathUtil.cleanNumber(totalInterest, 10, 0));
        const acresString = isNaN(totalAcres) ? '---' : fixNumberForUIDisplay(MathUtil.cleanNumber(totalAcres));
        const executiveRightsString = fixNumberForUIDisplay(MathUtil.cleanNumber(execRights, 7));

        setOwnershipInterestInfo(
            <div>
                {props.tractDetail?.nonExecutiveAdditionalOwnershipExists && (
                    <span
                        className="mr-1"
                        style={{ textAlign: 'left', display: 'inline-block', minWidth: '18.4rem', padding: '1rem', backgroundColor: execStatus }}
                    >
                        Total Executive Rights: {executiveRightsString}
                    </span>
                )}
                <span
                    className="mr-1"
                    style={{ textAlign: 'left', display: 'inline-block', minWidth: '18.4rem', padding: '1rem', backgroundColor: status }}
                >
                    Total Interest: {interestString}
                </span>
                <span
                    className="mr-1"
                    style={{ textAlign: 'left', display: 'inline-block', minWidth: '18.4rem', padding: '1rem', backgroundColor: status }}
                >
                    Total Acres: {acresString}
                </span>
            </div>
        );
    };

    const onUpdateOwnershipNote = async (ownership: Ownership) => {
        await put(`${Url.tract.getSurfaceTracts(appContext, appContext.sectionNumber, '')}/${appContext.surfaceTractNumber}/ownership-note`, ownership);

        if (response.ok) {
            props.loadSurfaceTract();
        }
    };

    const addOwnership = (parentOwnershipId: number = null) => {
        // TODO: Set this up for Mineral Tract projects, and consolidate if-statement to avoid duplicate code.
        if (props.ownershipType === 'Surface') {
            dialogContext.tract.createSurfaceOwnership(props.tractId, parentOwnershipId, async (ownership: Ownership) => {
                ownership.parentOwnershipId = parentOwnershipId;
                const result = await post(`${Url.tract.getTract(appContext)}/surfaceTracts/${props.tractId}/ownerships`, ownership);

                if (response.ok) {
                    ownership.id = result.id;
                }

                props.loadSurfaceTract();
            });
        } else if (props.ownershipType === 'Mineral') {
            dialogContext.tract.createMineralOwnership(
                'Add Ownership',
                'ADD_ADDITIONAL_OWNERSHIP',
                props.addDeviation,
                props.onNavigate,
                null,
                props.onSaveAddOwnership,
                props.tractDetail,
                props.horizonNumber,
                parentOwnershipId
            );
        }
    };

    const onCopyOwnership = () => {
        let contextName: 'ownership' | 'surfaceOwnership' | 'workingInterest';
        if (props.projectType === 'Surface') {
            contextName = 'surfaceOwnership';
        } else {
            contextName = 'ownership';
        }
        dialogContext.leasehold.copyOwnership(
            contextName,
            { projectNumber: appContext.projectNumber, clientNumber: appContext.clientNumber },
            massageCloneData
        );
    };

    const massageCloneData = (context: string, options: string) => {
        let current: string;
        if (props.projectType === 'Surface') {
            current = `${appContext.clientNumber}.${appContext.projectNumber}.${appContext.sectionNumber}.${appContext.surfaceTractNumber}`;
        } else {
            current = `${appContext.clientNumber}.${appContext.projectNumber}.${appContext.sectionNumber}.${appContext.tractNumber}-${appContext.horizonNumber}`;
        }

        const newOptions = JSON.parse(options);
        newOptions.targetSurfaceTract = current;

        cloneData(context, JSON.stringify(newOptions));
    };

    const cloneData = async (context: string, options: string) => {
        await post(`v1.0/clone-data`, { context, options });

        if (response.ok) {
            props.loadSurfaceTract();
        } else {
            window.alert('Copy Ownership failed.');
        }
    };

    const onOpenContactNotes = (ownership: Ownership) => {
        dialogContext.tract.openContactNotes(ownership, false, props.projectType);
    };

    const onOpenOwnershipNotes = (ownership: Ownership) => {
        dialogContext.tract.openOwnershipNotes(ownership, props.owners, '', 1, (updatedOwnership: Ownership) =>
            onUpdateOwnershipNote(updatedOwnership)
        );
    };

    const onNavigateLeaseInfo = (ownershipId: number) => {
        dialogContext.leasehold.manageLeaseholds(ownershipId, props.horizonNumber, (path: string) => {
            dispatch(push(window.location.pathname + path));
        });
    };

    const navigateAcquision = (ownerLookup: number) => {
        let seletedOwner: Owner = !!props.owners ? props.owners?.find((o) => o.ownerLookUpId === ownerLookup) : undefined;
        dialogContext.contract.addManageAgreement(
            appContext.clientNumber,
            appContext.projectNumber,
            ownerLookup,
            dialogContext.hideDialog,
            seletedOwner
        );
    };

    const containerHeader = () => {
        let buttonHeader = props.ownershipType === 'Surface' ? 'Surface' : 'Mineral';
        return (
            <>
                <fieldset style={{ padding: '.3rem 0', borderWidth: '1px 0 0 0' }}>
                    <legend className="legend">{props.ownershipType} Ownership Information</legend>
                </fieldset>
                <span className="warning">ALL INTEREST REPRESENTED AS DECIMAL</span>
                <div style={{ marginBottom: '1rem', marginLeft: '1rem' }}>
                    {props.ownershipType === props.projectType && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', textAlign: 'right', marginRight: '1rem' }}>
                            <Button
                                text={`Copy ${buttonHeader} Ownership`}
                                size="medium"
                                intent="primary"
                                onClick={() => onCopyOwnership()}
                                disabled={props?.formState.isDirty}
                            />
                            <Button
                                text={`Attach ${buttonHeader} Ownership`}
                                size="medium"
                                intent="primary"
                                onClick={() => addOwnership()}
                                disabled={props.formState.isDirty}
                            />
                        </div>
                    )}
                    <div>{ownershipInterestInfo}</div>
                </div>
            </>
        );
    };

    const renderOwnerships = () => {
        return (
            <CardContainer>
                {props.ownerships.map((os: Ownership, index: number) => {
                    const ownershipActions: ContextMenuAction[] = [
                        { text: 'Delete Ownership', action: () => deleteOwnership(os.id) },
                        { text: 'Manage Contract Ownership', action: () => onNavigateLeaseInfo(os.id) },
                        { text: 'Contact Notes', action: () => onOpenContactNotes(os) },
                        { text: 'Generate KUDU Contract', action: () => navigateAcquision(os.ownerLookUpId) },
                        { text: 'Ownership Notes', action: () => onOpenOwnershipNotes(os) },
                        { text: 'Add Additional Ownership', action: () => addOwnership(os.id) },
                    ];

                    return (
                        <div key={index}>
                            <GlobalOwnershipCard
                                index={index}
                                namePrefix={props.namePrefix}
                                contactCategories={props.ownershipContactCategories}
                                ownership={os}
                                owners={props.owners}
                                grossAcres={props.grossAcres}
                                projectType={props.projectType}
                                leaseBuyerList={props.leaseBuyerList}
                                register={props.register}
                                errors={props.errors}
                                errorMessage={''}
                                ownershipActions={ownershipActions}
                                hasNEMI={props.tractDetail?.nonExecutiveAdditionalOwnershipExists}
                                reset={() => props.reset()}
                                updateOwnershipInterest={() => updateOwnershipInterest()}
                                updateOwnershipNote={(ownership: Ownership) => onUpdateOwnershipNote(ownership)}
                                createAdditionalOwnership={(ownershipId: number) => addOwnership(ownershipId)}
                                openOwnershipNotes={(ownership: Ownership) => onOpenOwnershipNotes(ownership)}
                                deleteOwnership={(ownershipId: number) => deleteOwnership(ownershipId)}
                            />
                        </div>
                    );
                })}
            </CardContainer>
        );
    };

    const deleteOwnership = (ownershipId: number) => {
        dialogContext.leasehold.deleteOwnership(
            ownershipId,
            (path: string) => {
                dispatch(push(window.location.pathname + path));
            },
            () => {
                onConfirmDelete(ownershipId);
            }
        );
    };

    const onConfirmDelete = async (ownershipId: number) => {
        await del(
            `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/tracts/delete-ownership/${ownershipId}`
        );

        if (response.ok) {
            props.loadSurfaceTract();
        }
    };

    return (
        <>
            {props.ownerships?.length > 0 && !loading && (
                <>
                    {containerHeader()}
                    {renderOwnerships()}
                </>
            )}
            {props.ownerships?.length === 0 && !loading && (
                <>
                    {containerHeader()}
                    <div
                        style={{
                            margin: '1rem',
                            padding: '1rem',
                            height: '4rem',
                            fontWeight: 'bold',
                            border: '2px solid red',
                            fontSize: '1.1em',
                            textAlign: 'center',
                        }}
                    >
                        No Surface Ownerships found
                    </div>
                </>
            )}
            {loading && (
                <>
                    {containerHeader()}
                    <div
                        style={{
                            margin: '1rem',
                            padding: '1rem',
                            height: '4rem',
                            fontWeight: 'bold',
                            border: '2px solid #ccc',
                            fontSize: '1.1em',
                            textAlign: 'center',
                        }}
                    >
                        Loading ...
                    </div>
                </>
            )}
        </>
    );
};

export default GlobalOwnershipList;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/OwnershipList.redesign.tsx